import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import FormInput from "../../../../../components/Form/FormInput";
import DateTimeField from "../../../../../components/Form/DateTimeField";
import FormSelect from "../../../../../components/Form/FormSelect";
import TextEditor from "../../../../../components/Form/TextEditor";
import FormTextarea from "../../../../../components/Form/TextArea";
import TagMultiSelect from "../../../../../components/Form/TagMultiSelect";
import BackButton from "../../../../../components/Button/BackButton";
import { AuthContext } from "../../../../../context/AuthContext";
import { isFreelance, isProjectManager } from "../../../../../utils/constants/accountType";
import { btn } from "../../../../../components/Button/buttonStyles";

const EditProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers - skip the (enterprise-only) managers
  // fetch, hide the manager fields, and drop the primary-manager requirement.
  const freelance = isFreelance(user);
  const projectManager = isProjectManager(user);

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [clients, setClients] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);
  const [services, setServices] = useState([]);
  const [languagePairs, setLanguagePairs] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [managers, setManagers] = useState([]);

  const toLocalDateTimeInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };

  // Fetch existing project and all dropdown data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [
          projectRes,
          clientsRes,
          servicesRes,
          languagePairsRes,
          specializationsRes,
          managersRes,
        ] = await Promise.all([
          api.get(`/projects/${id}`, { withCredentials: true }),
          api.get("/clients", { withCredentials: true }),
          api.get("/admin-services", { withCredentials: true }),
          api.get("/admin-language-pairs", { withCredentials: true }),
          api.get("/admin-specializations", { withCredentials: true }),
          // Managers endpoint is enterprise-only; skip it for freelance accounts.
          // The full module is admin-only, so project managers read the trimmed
          // roster instead - fetching /managers as one would 403 and take the
          // whole page down with it.
          freelance
            ? Promise.resolve(null)
            : api.get(
                projectManager ? "/assignable-managers" : "/managers",
                { withCredentials: true }
              ),
        ]);

        const project = projectRes.data.data;

        setClients(clientsRes.data.data || []);
        setServices(
          (servicesRes.data.data || []).filter((s) => s.active_flag === true)
        );
        setLanguagePairs(languagePairsRes.data.data || []);
        setSpecializations(
          (specializationsRes.data.data || []).filter(
            (s) => s.active_flag === true
          )
        );
        setManagers(managersRes?.data?.data || []);

        // Pre-populate form from project data
        setForm({
          client_id: project.client?.id ? String(project.client.id) : "",
          client_contact_person_id: project.contactPerson?.id
            ? String(project.contactPerson.id)
            : "",
          project_name: project.project_name || "",
          service_id: project.service?.id ? String(project.service.id) : "",
          language_pair_ids: (project.languagePairs || []).map((lp) => lp.id),
          specialization_id: project.specialization?.id
            ? String(project.specialization.id)
            : "",
          start_at: toLocalDateTimeInput(project.start_at),
          deadline_at: toLocalDateTimeInput(project.deadline_at),
          instructions: project.instructions || "",
          internal_note: project.internal_note || "",
          primary_manager_id: project.primaryManager?.id
            ? String(project.primaryManager.id)
            : "",
          secondary_manager_id: project.secondaryManager?.id
            ? String(project.secondaryManager.id)
            : "",
        });
      } catch (err) {
        console.error("Failed to load project data:", err);
        setServerError("Failed to load project data.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id, freelance, projectManager]);

  // Re-fetch contact persons when client changes
  useEffect(() => {
    if (!form?.client_id) {
      setContactPersons([]);
      return;
    }

    const fetchContactPersons = async () => {
      try {
        const res = await api.get(
          `/client/contact-persons/${form.client_id}`,
          { withCredentials: true }
        );
        setContactPersons(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch contact persons:", err);
        setContactPersons([]);
      }
    };

    fetchContactPersons();
  }, [form?.client_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // When client changes, reset contact person
    if (name === "client_id") {
      setForm((prev) => ({
        ...prev,
        client_id: value,
        client_contact_person_id: "",
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
    setServerError("");
  };

  const validate = () => {
    const err = {};
    if (!form.client_id) err.client_id = "Client is required.";
    if (!form.project_name?.trim())
      err.project_name = "Project name is required.";
    if (!form.start_at) err.start_at = "Start date is required.";
    if (!form.deadline_at) err.deadline_at = "Deadline is required.";
    if (!freelance && !form.primary_manager_id)
      err.primary_manager_id = "Primary manager is required.";
    if (!form.language_pair_ids || form.language_pair_ids.length === 0)
      err.language_pair_ids = "At least one language pair is required.";
    if (
      form.start_at &&
      form.deadline_at &&
      form.deadline_at <= form.start_at
    ) {
      err.deadline_at = "Deadline must be after start date.";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setServerError("");

    try {
      const payload = {
        ...form,
        client_contact_person_id: form.client_contact_person_id || null,
        service_id: form.service_id || null,
        specialization_id: form.specialization_id || null,
        secondary_manager_id: form.secondary_manager_id || null,
        instructions: form.instructions || null,
        internal_note: form.internal_note || null,
      };

      await api.put(`/projects/${id}`, payload, { withCredentials: true });

      setSuccess("Project updated successfully!");
      setTimeout(() => {
        navigate(`/project/${id}`);
      }, 1500);
    } catch (err) {
      console.error("Update project error:", err);
      setServerError(
        err.response?.data?.message || "Failed to update project."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-10 text-gray-500">Loading project...</div>
    );
  }

  if (!form) {
    return (
      <div className="text-center mt-10 text-danger-600 font-medium">
        {serverError || "Project not found."}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white shadow rounded-lg p-8 space-y-6"
    >
      <div className="flex items-center gap-3 mb-2">
        <BackButton />
        <h2 className="text-xl font-bold text-gray-900">Edit Project</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormSelect
          label="Client"
          name="client_id"
          value={form.client_id}
          onChange={handleChange}
          options={clients.map((client) => ({
            value: client.id,
            label:
              client.type === "Company"
                ? client.company_name
                : `${client.primary_user?.first_name || ""} ${
                    client.primary_user?.last_name || ""
                  }`.trim(),
          }))}
          error={errors.client_id}
          required
        />

        <FormSelect
          label="Client Contact Person"
          name="client_contact_person_id"
          value={form.client_contact_person_id}
          onChange={handleChange}
          options={contactPersons.map((p) => ({
            value: p.id,
            label: `${p.first_name} ${p.last_name}`,
          }))}
          disabled={!form.client_id}
        />

        <FormInput
          label="Project Name"
          name="project_name"
          value={form.project_name}
          onChange={handleChange}
          error={errors.project_name}
          required
        />

        <FormSelect
          label="Service"
          name="service_id"
          value={form.service_id}
          onChange={handleChange}
          options={services.map((s) => ({
            value: s.id,
            label: s.name,
          }))}
        />

        <div className="md:col-span-2">
          <TagMultiSelect
            label="Language Pairs"
            required
            value={form.language_pair_ids}
            onChange={(ids) => {
              setForm((prev) => ({ ...prev, language_pair_ids: ids }));
              setErrors((prev) => ({ ...prev, language_pair_ids: "" }));
            }}
            options={languagePairs.map((lp) => ({
              value: lp.id,
              label: `${lp.sourceLanguage?.name} → ${lp.targetLanguage?.name}`,
            }))}
            placeholder="Select language pairs"
            error={errors.language_pair_ids}
            emptyMessage="No language pairs configured"
          />
        </div>

        <FormSelect
          label="Specialization"
          name="specialization_id"
          value={form.specialization_id}
          onChange={handleChange}
          options={specializations.map((s) => ({
            value: s.id,
            label: s.name,
          }))}
        />

        <DateTimeField
          label="Start At"
          name="start_at"
          value={form.start_at}
          onChange={handleChange}
          error={errors.start_at}
          required
        />

        <DateTimeField
          label="Deadline At"
          name="deadline_at"
          value={form.deadline_at}
          onChange={handleChange}
          error={errors.deadline_at}
          min={form.start_at}
          required
        />

        {/* Managers are enterprise-only; hidden for freelance accounts. */}
        {!freelance && (
          <>
            <FormSelect
              label="Primary Manager"
              name="primary_manager_id"
              value={form.primary_manager_id}
              onChange={handleChange}
              options={managers.map((m) => ({
                value: m.id,
                label: `${m.first_name} ${m.last_name}`,
              }))}
              error={errors.primary_manager_id}
              required
            />

            <FormSelect
              label="Secondary Manager"
              name="secondary_manager_id"
              value={form.secondary_manager_id}
              onChange={handleChange}
              options={managers
                .filter((m) => m.id !== Number(form.primary_manager_id))
                .map((m) => ({
                  value: m.id,
                  label: `${m.first_name} ${m.last_name}`,
                }))}
            />
          </>
        )}
      </div>

      <TextEditor
        label="Instructions"
        value={form.instructions}
        onChange={(val) =>
          setForm((prev) => ({ ...prev, instructions: val }))
        }
      />

      <FormTextarea
        label="Internal Note"
        name="internal_note"
        value={form.internal_note}
        onChange={handleChange}
        rows={3}
        placeholder="Internal notes (not visible to client)"
      />

      {serverError && (
        <div className="bg-danger-100 text-danger-700 border border-danger-400 rounded p-3 text-center font-semibold">
          {serverError}
        </div>
      )}

      {success && (
        <div className="bg-green-100 text-green-700 border border-green-400 rounded p-3 text-center font-semibold">
          {success}
        </div>
      )}

      <span className="text-gray-500 text-sm mt-1">
        Fields marked with <span className="text-danger-600">*</span> are
        mandatory.
      </span>

      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className={btn("primary", "sm")}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </button>

        <button
          type="button"
          onClick={() => navigate(`/project/${id}`)}
          disabled={isSubmitting}
          className={btn("secondary", "sm")}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default EditProjectDetails;